/**
 * This modal appears when an entry is cloned from a template. It displays fields for selecting the template
 * to clone from, entering the name of the entry, the folder the entry will be created in, and a schema
 * and schema field values for the entry.
 */

import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import moment from 'moment';
import React, {useEffect, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import BenchlingConfig from 'BenchlingConfig';
import {COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  calcTagTableErrorsByFieldID,
  TagTableErrorsByFieldId,
  useTagTableState,
} from 'metadata/useTagTableState';
import {useEntryTemplate} from 'notebook/entry-templates/hooks/useEntryTemplate';
import {getTagsToSet, TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';
import {
  StudyAssociationInputProps,
  useStudyAssociationInputProps,
  getStudyAssociations,
} from 'studies/StudyAssociationInput';
import {StudySearchItem} from 'studies/StudySearchInput';
import Modal from 'ui-library/modals/Modal';
import {MOMENT_DATE_FORMAT} from 'util/DateConstants';

import CreateEntryFromTemplateModalContents from './CreateEntryFromTemplateModalContents';

export const SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY: DocumentNode = gql`
  query CreateEntryFromTemplateQuery($id: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    template(id: $id) {
      ... on TemplateNode {
        name
        id
        tagsJson
        tagSchema {
          ...TagSchemaGraphQL
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Custom hook to keep date formatting logic within Notebook code when being reused.
 */
export function useInitialEntryDay(): {initialDay: string; setInitialDay: (day: string) => void} {
  const [initialDay, setInitialDay] = useState(moment().format(MOMENT_DATE_FORMAT));
  return {initialDay, setInitialDay};
}

export interface CreateEntryFromTemplateModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (arg: {
    entryName: string;
    folderId: string;
    templateId: string;
    tagSchemaId?: string;
    schemaTags?: TagUpdateObject;
    initialDay: string;
    studyAssociations?: Array<StudyAssociation>;
  }) => void;
  initialTemplateId?: string;
  initialFolderId?: string | null;
  isTemplateSelectorDisabled: boolean;
  initialStudySearchItems?: Array<StudySearchItem> | null;
  filterTemplateIds?: Array<string> | null;
  showUnpublishedCollectibles?: boolean;
  displayChangeStudyableAssignmentPermissionWarning?: boolean;
  initialAutoLinkingStudy?: {
    id: string;
    name: string;
  };
}

export function isCreationDisabled(
  templateId: string | null,
  entryName: string,
  folderId: string | null | undefined,
  templateQueryResultType: QueryResultType,
  hasSchema: boolean,
  errorsByFieldId: TagTableErrorsByFieldId,
  isMissingChangeStudyableAssignmentPermission?: boolean
): boolean {
  return (
    templateId == null ||
    !entryName.length ||
    folderId == null ||
    templateQueryResultType === QueryResultType.PENDING ||
    // Disable if a schema is required for review but none have been attached
    (BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW ? !hasSchema : false) ||
    // Disable if required fields are required but not all present
    (BenchlingConfig.ELN_ENFORCE_REQUIRED_FIELDS_ON_ENTRY_CREATE &&
      Object.keys(errorsByFieldId).length > 0) ||
    // disable if the user is missing the change studyable assignment permission for a study folder
    isMissingChangeStudyableAssignmentPermission === true
  );
}

interface OnRequestSubmitParams {
  entryName: string;
  folderId: string;
  templateId: string;
  tagSchemaId?: string;
  schemaTags?: TagUpdateObject;
  initialDay: string;
  studyAssociations?: Array<StudyAssociation>;
}

function CreateEntryFromTemplateModal(props: CreateEntryFromTemplateModalProps): JSX.Element {
  const [entryName, setEntryName] = useState('');
  const [folderId, setFolderId] = useState(props.initialFolderId);
  const {tagSchema, schemaTags, updateTagSchema, updateSchemaTag} = useTagTableState();
  const {
    templateId,
    changeTemplate,
    templateResult: result,
    canChangeSchema,
  } = useEntryTemplate(props.initialTemplateId || null, setEntryName, updateTagSchema);
  const {initialDay, setInitialDay} = useInitialEntryDay();
  const studyAssociationInputProps: StudyAssociationInputProps = useStudyAssociationInputProps({
    initialStudySearchItems: props.initialStudySearchItems ?? [],
    displayPermissionWarning: props.displayChangeStudyableAssignmentPermissionWarning,
    initialAutoLinkingStudy: props.initialAutoLinkingStudy,
  });

  useEffect(() => {
    analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.ELN, {
      modalType: 'create_entry_from_template',
    });
  }, []);

  const handleSubmit = (): void => {
    // Typecheck to apease typescript, submit is disabled if these fields are null
    if (folderId == null || templateId == null) {
      return;
    }
    const studyAssociations = getStudyAssociations(studyAssociationInputProps);
    FolderDefaults.set(FolderDefaultsKey.NOTEBOOK, folderId);
    const onRequestSubmitParams: OnRequestSubmitParams = {
      entryName,
      folderId,
      templateId,
      tagSchemaId: tagSchema?.id,
      schemaTags: getTagsToSet(schemaTags),
      initialDay,
    };
    if (studyAssociations.length !== 0) {
      onRequestSubmitParams.studyAssociations = studyAssociations;
    }
    props.onRequestSubmit(onRequestSubmitParams);
  };

  const errorsByFieldId = tagSchema ? calcTagTableErrorsByFieldID(tagSchema, schemaTags) : {};
  return (
    <Modal
      title="Create entry from template"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: 'Create',
          submit: true,
          disabled: isCreationDisabled(
            templateId,
            entryName,
            folderId,
            result.type,
            tagSchema != null,
            errorsByFieldId,
            studyAssociationInputProps.invalidItemNames.length > 0
          ),
        },
      ]}
    >
      <CreateEntryFromTemplateModalContents
        isTemplateSelectorDisabled={props.isTemplateSelectorDisabled}
        templateId={templateId}
        changeTemplate={changeTemplate}
        initialDay={initialDay}
        setInitialDay={setInitialDay}
        entryName={entryName}
        setEntryName={setEntryName}
        folderId={folderId}
        setFolderId={setFolderId}
        queryIsPending={result.type === QueryResultType.PENDING}
        canChangeSchema={canChangeSchema}
        schemaTags={schemaTags}
        tagSchema={tagSchema}
        updateTagSchema={updateTagSchema}
        updateSchemaTag={updateSchemaTag}
        errorsByFieldId={errorsByFieldId}
        studyAssociationInputProps={studyAssociationInputProps}
        enableAutoLinkingSelection={props.initialAutoLinkingStudy === undefined}
        filterTemplateIds={props.filterTemplateIds || undefined}
        showUnpublishedCollectibles={props.showUnpublishedCollectibles}
      />
    </Modal>
  );
}

export default CreateEntryFromTemplateModal;
